import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDebouncedSearchText } from "./useDebouncedSearchText";
import {
  normalizeTenantsQueryArgs,
  useGetTenantsQuery,
  TenantsQueryParams,
} from "../store/services/tenants";
import { TenantListItem } from "../models/users";

type TenantsQueryOptions = Parameters<typeof useGetTenantsQuery>[1];
type TenantsQueryResult = ReturnType<typeof useGetTenantsQuery>;
type TypedTenantsQueryResult = Omit<TenantsQueryResult, "data"> & {
  data: TenantListItem[] | undefined;
};

export function useDebouncedTenantsQuery(
  params: TenantsQueryParams | void,
  options?: TenantsQueryOptions
): TypedTenantsQueryResult {
  const { provider, theme, limit } = params ?? {};
  const debouncedProvider = useDebouncedSearchText(provider);
  const debouncedTheme = useDebouncedSearchText(theme);

  const isDebouncing =
    (provider ?? "") !== debouncedProvider ||
    (theme ?? "") !== debouncedTheme;

  const queryArgs = useMemo(
    () =>
      normalizeTenantsQueryArgs({
        provider: debouncedProvider,
        theme: debouncedTheme,
        limit,
      }),
    [debouncedProvider, debouncedTheme, limit]
  );

  const shouldSkip = Boolean(options?.skip) || isDebouncing;

  const queryResult = useGetTenantsQuery(
    shouldSkip ? skipToken : queryArgs,
    options
  );

  const refetch = (() => {
    if (!shouldSkip) return queryResult.refetch();
  }) as typeof queryResult.refetch;

  return { ...queryResult, refetch } as unknown as TypedTenantsQueryResult;
}
